import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Paper, Box, Grid, Chip, Typography, Autocomplete, TextField, CircularProgress,
  Radio, RadioGroup, FormControlLabel, FormControl, Divider, /* Checkbox, */
} from '@mui/material';
import ApiService from '../../functions/apiService';
import ButtonTheme from '../../components/ThemeButton/ThemeButton';
import { toast } from 'react-toastify';
import moment from 'moment';

/**
 * Recurring Position Assignment
 * ------------------------------------------------------------------
 * UI build with real dropdown data. No page/config gate yet — the
 * config key will be added to the <Menu.Item /> and the route later.
 *
 * Reused endpoints (same as the Position Assignment page):
 *   GET /position-assignment/list  -> position-assignment dropdown
 *   GET /employees-list?search=    -> employee dropdown
 *   GET /shiftTypes                -> shifts (fetched only after a PA is picked)
 */

const RED = '#BF1D2D';
const FULL_DAY_CODE = 1; // 24-hour shift; mutually exclusive with the others

// values map to PHP date('N'): Mon = 1 … Sun = 7
const WEEKDAYS = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 7 },
];

const SHIFT_LABEL_OVERRIDES = { [FULL_DAY_CODE]: 'Full Day' };
const shiftLabel = (code, name) => {
  if (SHIFT_LABEL_OVERRIDES[code]) return SHIFT_LABEL_OVERRIDES[code];
  const n = (name || '').trim();
  if (!n) return `Shift ${code}`;
  if (/shift/i.test(n)) return n; // already reads like "Day Shift"
  if (/^(day|night|evening|morning|afternoon)$/i.test(n)) return `${n} Shift`;
  return n;
};

const fieldLabel = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#48535B', marginBottom: '6px' };

// report status badge — derived from the result row's `filled` flag
const STATUS_STYLE = {
  FILLED: { color: '#01784F', bg: 'rgba(1,120,79,.12)' },
  SKIPPED: { color: '#B26A00', bg: 'rgba(178,106,0,.14)' },
};
const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[(status || '').toUpperCase()] || { color: '#48535B', bg: '#EEF0F2' };
  return (
    <span style={{
      fontSize: '11px', fontWeight: 700, color: s.color, background: s.bg,
      padding: '3px 9px', borderRadius: '10px', letterSpacing: '.3px', textTransform: 'uppercase',
    }}>
      {status}
    </span>
  );
};

const errorText = { fontSize: '12px', color: RED, marginTop: '4px' };

// required-field marker (matches the app's `<span style={{ color: "red" }}>*</span>` convention)
const Req = () => <span style={{ color: RED }}>*</span>;

// resolve a report member entry (removed/occupied) to a display name.
// Backend sends an object with Emp_Full_Name/Emp_Rank/Emp_Name; falls back to
// the loaded employee list, then the raw code.
const displayMember = (m, employeeOptions) => {
  if (m && typeof m === 'object') {
    return (
      m.Emp_Full_Name ||
      [m.Emp_Rank, m.Emp_Name].filter(Boolean).join(' ').trim() ||
      m.Emp_Name || m.name || m.label || String(m.Emp_Code ?? m.emp_code ?? '')
    );
  }
  return employeeOptions.find((e) => String(e.id) === String(m))?.label || m;
};

// name plus the displaced member's own held times, e.g. "Lt. Michael Reyes (18:00–07:00)"
const displayMemberWithTime = (m, employeeOptions) => {
  const name = displayMember(m, employeeOptions);
  if (m && typeof m === 'object' && m.Start_Time && m.End_Time) {
    const hhmm = (t) => String(t).slice(0, 5);
    return `${name} (${hhmm(m.Start_Time)}–${hhmm(m.End_Time)})`;
  }
  return name;
};

// a numbered form section with a title + helper
const Section = ({ step, title, desc, children }) => (
  <Box sx={{ mb: 3.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '50%', background: RED, color: '#fff',
        fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {step}
      </Box>
      <Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1f2328', lineHeight: 1.2 }}>{title}</Typography>
        {desc && <Typography sx={{ fontSize: '12.5px', color: '#8A9199' }}>{desc}</Typography>}
      </Box>
    </Box>
    <Box sx={{ pl: { xs: 0, sm: '38px' } }}>{children}</Box>
  </Box>
);

const RecurringPositionAssignment = () => {
  // dropdown data
  const [assignmentOptions, setAssignmentOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]); // { code, name, label }
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingShifts, setLoadingShifts] = useState(false);

  // form state
  const [positionAssignment, setPositionAssignment] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [selectedShiftCodes, setSelectedShiftCodes] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);
  const [hours, setHours] = useState('full'); // 'full' | 'part'
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('14:00');
  const [conflictMode, setConflictMode] = useState('skip'); // 'skip' | 'force'
  // Hidden for now — re-enable with the Options section below:
  // const [checkSignup, setCheckSignup] = useState(false);
  // const [applyHours, setApplyHours] = useState(false);

  // validation + report
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // API `result` payload (null = not run yet)
  const [runMeta, setRunMeta] = useState(null); // snapshot of what was run, for the report header
  const reportRef = useRef(null);

  // ---- fetch dropdown data (assignments + employees on mount) ----
  const loadAssignments = () => {
    setLoadingAssignments(true);
    ApiService.get('/position-assignment/list')
      .then((data) => {
        const rows = data?.data?.data || [];
        const options = rows.map((item) => {
          const label =
            [item.Assignment_Name, item.Position_Name].filter(Boolean).join(' · ') ||
            `Assignment #${item.ID}`;
          const meta = [item.Assignment_Name, item.Position_Name, item.Vehicle_Name]
            .filter(Boolean)
            .join(' · ');
          const shiftCodes = item.Shift_Code
            ? String(item.Shift_Code).split(',').map((c) => Number(String(c).trim()))
            : [];
          return { ...item, id: item.ID, label, meta, shiftCodes };
        });
        setAssignmentOptions(options);
      })
      .catch(() => {})
      .finally(() => setLoadingAssignments(false));
  };

  const loadEmployees = () => {
    setLoadingEmployees(true);
    ApiService.get('/employees-list?search=')
      .then(({ data }) => {
        const options = (data?.data || []).map((emp) => ({
          ...emp,
          id: emp.Emp_Code,
          label: `${emp?.employee_assignment?.EA_Rank || ''} ${emp.fullName || ''}`.trim(),
        }));
        setEmployeeOptions(options);
      })
      .catch(() => {})
      .finally(() => setLoadingEmployees(false));
  };

  useEffect(() => {
    loadAssignments();
    loadEmployees();
  }, []);

  // shifts are fetched lazily — only after a position assignment is chosen
  const ensureShiftTypes = () => {
    if (shiftTypes.length) return Promise.resolve(shiftTypes);
    setLoadingShifts(true);
    return ApiService.get('/shiftTypes')
      .then((data) => {
        const options = (data?.data?.data || []).map((shift) => ({
          code: shift.ShiftCode,
          name: shift.Shift,
          label: shiftLabel(shift.ShiftCode, shift.Shift),
        }));
        setShiftTypes(options);
        return options;
      })
      .catch(() => [])
      .finally(() => setLoadingShifts(false));
  };

  const onSelectPositionAssignment = (value) => {
    setPositionAssignment(value);
    setSelectedShiftCodes([]); // default: nothing selected — the user picks
    if (value) ensureShiftTypes();
  };

  // shifts offered: the assignment's own codes, or EVERY shift we have when it has none
  const availableShifts = useMemo(() => {
    if (!positionAssignment) return [];
    const codes = positionAssignment.shiftCodes || [];
    if (codes.length > 0) {
      return codes.map((code) => {
        const match = shiftTypes.find((s) => s.code === code);
        return { code, label: match ? match.label : shiftLabel(code) };
      });
    }
    return shiftTypes.map((s) => ({ code: s.code, label: s.label }));
  }, [positionAssignment, shiftTypes]);

  const shiftsAreFallback =
    !!positionAssignment && (positionAssignment.shiftCodes || []).length === 0;

  const isFullDaySelected = selectedShiftCodes.includes(FULL_DAY_CODE);
  const hasSpecificSelected = selectedShiftCodes.some((c) => c !== FULL_DAY_CODE);

  // Full Day (24h) is exclusive: picking it clears the rest; picking any
  // specific shift clears Full Day.
  const toggleShift = (code) => {
    setSelectedShiftCodes((prev) => {
      if (code === FULL_DAY_CODE) {
        return prev.includes(FULL_DAY_CODE) ? [] : [FULL_DAY_CODE];
      }
      const base = prev.filter((c) => c !== FULL_DAY_CODE);
      return base.includes(code) ? base.filter((c) => c !== code) : [...base, code];
    });
  };

  const toggleWeekday = (v) => {
    setSelectedWeekdays((prev) =>
      prev.includes(v) ? prev.filter((d) => d !== v) : [...prev, v].sort((a, b) => a - b)
    );
  };

  // client-side validation — everything required except the To (end) date
  const validate = () => {
    const e = {};
    if (!positionAssignment) e.positionAssignment = 'Position assignment is required.';
    if (!employee) e.employee = 'Employee is required.';
    if (selectedShiftCodes.length === 0) e.shifts = 'Select at least one shift.';
    if (!fromDate) e.fromDate = 'From date is required.';
    if (!toDate) e.toDate = 'To date is required.';
    if (selectedWeekdays.length === 0) e.weekdays = 'Select at least one weekday.';
    if (hours === 'part') {
      if (!startTime) e.startTime = 'Start time is required.';
      if (!endTime) e.endTime = 'End time is required.';
    }
    return e;
  };

  const resetForm = () => {
    setPositionAssignment(null);
    setEmployee(null);
    setSelectedShiftCodes([]);
    setFromDate('');
    setToDate('');
    setSelectedWeekdays([]);
    setHours('full');
    setStartTime('08:00');
    setEndTime('14:00');
    setConflictMode('skip');
    setErrors({});
  };

  const handleAssign = () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return; // skip the API call when required fields are missing

    const isFullTime = hours === 'full';
    // "Full Day" (24h, code 1) expands to every fetched shift code except itself
    const shiftCodes = selectedShiftCodes.includes(FULL_DAY_CODE)
      ? shiftTypes.filter((s) => s.code !== FULL_DAY_CODE).map((s) => Number(s.code))
      : selectedShiftCodes.map(Number);

    const payload = {
      Emp_Code: employee.id,
      Position_Assignment_Id: positionAssignment.id,
      Shift_Codes: shiftCodes,                      // integers; Full Day → all other codes
      Start_Date: fromDate,                         // 'Y-m-d', required
      End_Date: toDate || null,                     // 'Y-m-d', nullable
      Week_Days: selectedWeekdays,                  // integers 1–7 (Mon=1 … Sun=7)
      isFullTime,                                   // boolean
      Start_Time: isFullTime ? null : startTime,    // required when part-time
      End_Time: isFullTime ? null : endTime,        // required when part-time
      Force_Assign: conflictMode === 'force',       // false = skip occupied, true = overwrite
      // Check_Signup: checkSignup,                 // nullable boolean (hidden for now)
      // Apply_Hours: applyHours,                   // nullable boolean (hidden for now)
    };

    setRunMeta({
      member: employee.label,
      assignment: positionAssignment.label,
      from: fromDate,
      to: toDate,
      weekdays: selectedWeekdays
        .map((v) => WEEKDAYS.find((d) => d.value === v)?.label)
        .filter(Boolean)
        .join(', '),
    });
    setResult(null);
    setSubmitting(true);
    reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // NOTE: ApiService.post resolves to the response BODY (not the axios response)
    ApiService.post('/volunteer-recurring-assign-position', payload)
      .then((res) => {
        setResult(res?.result || []);
        if (res?.message) toast.success(res.message);
        resetForm(); // clear the form after a successful run (report stays visible)
      })
      .catch((err) => {
        toast.error(err?.data?.message || 'Failed to assign shifts');
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Box sx={{ mt: '1rem' }}>
      <Paper className="p-4">
        {/* simple heading */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Recurring Position Assignment</Typography>
          <Typography sx={{ fontSize: '13px', color: '#8A9199', mt: 0.5 }}>
            Assign a member to a position assignment across a repeating date range.
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* 1 · Assignment & member */}
        <Section step="1" title="Assignment & member" desc="Choose the position assignment, the member, and which shifts to fill.">
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <label style={fieldLabel}>Position assignment <Req /></label>
              <Autocomplete
                options={assignmentOptions}
                value={positionAssignment}
                onChange={(e, v) => onSelectPositionAssignment(v)}
                getOptionLabel={(o) => o.label || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                loading={loadingAssignments}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select position assignment"
                    size="small"
                    error={!!errors.positionAssignment}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAssignments ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {errors.positionAssignment ? (
                <Typography sx={errorText}>{errors.positionAssignment}</Typography>
              ) : (
                <Typography sx={{ fontSize: '12px', color: '#8A9199', mt: 0.75 }}>
                  {positionAssignment
                    ? positionAssignment.meta
                    : 'Station, unit, position & rotation come from the assignment.'}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <label style={fieldLabel}>Employee <Req /></label>
              <Autocomplete
                options={employeeOptions}
                value={employee}
                onChange={(e, v) => setEmployee(v)}
                getOptionLabel={(o) => o.label || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                loading={loadingEmployees}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search member"
                    size="small"
                    error={!!errors.employee}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingEmployees ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {errors.employee && <Typography sx={errorText}>{errors.employee}</Typography>}
            </Grid>

            {/* Shifts */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#48535B' }}>Shifts <Req /></Typography>
                {positionAssignment && selectedShiftCodes.length > 0 && (
                  <Typography sx={{ fontSize: '12px', color: RED, fontWeight: 600 }}>
                    {selectedShiftCodes.length} selected
                  </Typography>
                )}
              </Box>

              {!positionAssignment ? (
                <Typography sx={{ fontSize: '13px', color: '#B0B6BD' }}>
                  Select a position assignment to see its shifts.
                </Typography>
              ) : loadingShifts ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#8A9199' }}>
                  <CircularProgress size={16} color="inherit" />
                  <Typography sx={{ fontSize: '13px' }}>Loading shifts…</Typography>
                </Box>
              ) : availableShifts.length === 0 ? (
                <Typography sx={{ fontSize: '13px', color: '#B0B6BD' }}>No shifts found.</Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                    {availableShifts.map((s) => {
                      const active = selectedShiftCodes.includes(s.code);
                      const isFullDay = s.code === FULL_DAY_CODE;
                      const disabled = isFullDay ? hasSpecificSelected : isFullDaySelected;
                      return (
                        <Chip
                          key={s.code}
                          label={s.label}
                          disabled={disabled}
                          onClick={() => { if (!disabled) toggleShift(s.code); }}
                          variant={active ? 'filled' : 'outlined'}
                          sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            height: '38px',
                            px: 0.5,
                            borderColor: active ? RED : '#D0D5DD',
                            color: active ? RED : '#48535B',
                            backgroundColor: active ? 'rgba(191,29,45,.08)' : '#fff',
                            opacity: disabled ? 0.5 : 1,
                            '&:hover': { backgroundColor: active ? 'rgba(191,29,45,.14)' : '#F1F2F4' },
                          }}
                        />
                      );
                    })}
                  </Box>
                  <Typography sx={{ fontSize: '12px', color: '#8A9199', mt: 1 }}>
                    {shiftsAreFallback && 'This assignment has no specific shift — showing all shifts. '}
                    “Full Day” (24h) can’t be combined with other shifts.
                  </Typography>
                </>
              )}
              {errors.shifts && <Typography sx={errorText}>{errors.shifts}</Typography>}
            </Grid>
          </Grid>
        </Section>

        <Divider />
        <Box sx={{ height: 20 }} />

        {/* 2 · Schedule */}
        <Section step="2" title="Schedule" desc="The date range and the weekdays to fill within it.">
          <Grid container spacing={2.5}>
            <Grid item xs={6} sm={4} md={3}>
              <label style={fieldLabel}>From <Req /></label>
              <TextField
                type="date" size="small" fullWidth value={fromDate} error={!!errors.fromDate}
                onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }}
              />
              {errors.fromDate && <Typography sx={errorText}>{errors.fromDate}</Typography>}
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <label style={fieldLabel}>To <Req /></label>
              <TextField
                type="date" size="small" fullWidth value={toDate} error={!!errors.toDate}
                onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }}
              />
              {errors.toDate && <Typography sx={errorText}>{errors.toDate}</Typography>}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#48535B' }}>Weekdays <Req /></Typography>
                <Typography sx={{ fontSize: '12px', color: '#8A9199' }}>
                  — which days to fill between the dates
                </Typography>
                {selectedWeekdays.length > 0 && (
                  <Typography sx={{ fontSize: '12px', color: RED, fontWeight: 600 }}>
                    {selectedWeekdays.length} selected
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {WEEKDAYS.map((d) => {
                  const active = selectedWeekdays.includes(d.value);
                  return (
                    <Box
                      key={d.value}
                      component="button"
                      type="button"
                      onClick={() => toggleWeekday(d.value)}
                      sx={{
                        minWidth: 56, height: 40, px: 1.5, cursor: 'pointer', fontFamily: 'Karla, sans-serif',
                        fontSize: '13px', fontWeight: 700, borderRadius: '20px',
                        border: active ? `1px solid ${RED}` : '1px solid #D0D5DD',
                        color: active ? '#fff' : '#48535B',
                        backgroundColor: active ? RED : '#fff',
                        transition: 'background-color .12s, border-color .12s',
                      }}
                    >
                      {d.label}
                    </Box>
                  );
                })}
              </Box>
              {errors.weekdays && <Typography sx={errorText}>{errors.weekdays}</Typography>}
            </Grid>
          </Grid>
        </Section>

        <Divider />
        <Box sx={{ height: 20 }} />

        {/* 3 · Assign Type */}
        <Section step="3" title="Assign Type" desc="Full-time, or part-time with specific start and end times.">
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
            <FormControl sx={{ mt: 1 }}>
              <RadioGroup
                row value={hours} onChange={(e) => setHours(e.target.value)}
                sx={{ '& .MuiRadio-root.Mui-checked': { color: RED } }}
              >
                <FormControlLabel value="full" control={<Radio size="small" />} label="Full-time" />
                <FormControlLabel value="part" control={<Radio size="small" />} label="Part-time" />
              </RadioGroup>
            </FormControl>

            {hours === 'part' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box>
                  <label style={fieldLabel}>Start <Req /></label>
                  <TextField
                    type="time" size="small" value={startTime} error={!!errors.startTime}
                    onChange={(e) => setStartTime(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 130 }}
                  />
                  {errors.startTime && <Typography sx={errorText}>{errors.startTime}</Typography>}
                </Box>
                <Box>
                  <label style={fieldLabel}>End <Req /></label>
                  <TextField
                    type="time" size="small" value={endTime} error={!!errors.endTime}
                    onChange={(e) => setEndTime(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 130 }}
                  />
                  {errors.endTime && <Typography sx={errorText}>{errors.endTime}</Typography>}
                </Box>
              </Box>
            )}
          </Box>
        </Section>

        <Divider />
        <Box sx={{ height: 20 }} />

        {/* 4 · On conflict */}
        <Section step="4" title="On conflict" desc="What to do when a slot is already taken.">
          <Grid container spacing={2} sx={{ maxWidth: 620 }}>
            {[
              { key: 'skip', title: 'Skip occupied', desc: 'Leave slots held by another member untouched.' },
              { key: 'force', title: 'Force assign', desc: 'Overwrite a slot another member already holds.' },
            ].map((opt) => {
              const active = conflictMode === opt.key;
              return (
                <Grid item xs={12} sm={6} key={opt.key}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setConflictMode(opt.key)}
                    sx={{
                      width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: 'Karla, sans-serif',
                      border: active ? `1.5px solid ${RED}` : '1px solid #D0D5DD',
                      backgroundColor: active ? 'rgba(191,29,45,.05)' : '#fff',
                      borderRadius: '8px', p: 2, display: 'flex', alignItems: 'flex-start', gap: 1.25,
                      transition: 'border-color .15s, background-color .15s',
                      boxShadow: active ? '0 1px 4px rgba(191,29,45,.12)' : 'none',
                    }}
                  >
                    <Box sx={{
                      mt: '2px', width: 16, height: 16, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
                      border: active ? `5px solid ${RED}` : '1.5px solid #C4C4C4',
                    }} />
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: active ? RED : '#1f2328' }}>
                        {opt.title}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#8A9199', mt: 0.25, lineHeight: 1.4 }}>
                        {opt.desc}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Section>

        {/* 5 · Options (Check signup / Apply hours) hidden for now — to restore, uncomment the
            checkSignup/applyHours state, the Check_Signup/Apply_Hours payload keys, the Checkbox
            import, and re-add an Options <Section> here with two <Checkbox> FormControlLabels. */}

        {/* actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, pt: 2.5, borderTop: '1px solid #EDEFF2' }}>
          <ButtonTheme type="button" variant="filled" color="primary" className="px-5" onClick={handleAssign} disabled={submitting}>
            {submitting ? 'Assigning…' : 'Assign shifts'}
          </ButtonTheme>
          <ButtonTheme variant="filled" color="white" className="px-5">Cancel</ButtonTheme>
          {Object.keys(errors).length > 0 && (
            <Typography sx={{ fontSize: '13px', color: RED, fontWeight: 600, ml: 'auto' }}>
              Please fill the required fields.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* ===================== REPORT ===================== */}
      <Paper ref={reportRef} className="p-4" sx={{ mt: 2.5, scrollMarginTop: '16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>Report</Typography>
          {runMeta && (result || submitting) && (
            <Typography sx={{ fontSize: '13px', color: '#8A9199' }}>
              {runMeta.member} · {runMeta.assignment}
              {runMeta.from && ` · ${moment(runMeta.from).format('MMM D')}`}
              {runMeta.to && `–${moment(runMeta.to).format('MMM D, YYYY')}`}
              {runMeta.weekdays && ` · ${runMeta.weekdays}`}
            </Typography>
          )}
        </Box>

        {submitting ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6, color: '#8A9199' }}>
            <CircularProgress size={20} color="inherit" />
            <Typography sx={{ fontSize: '14px' }}>Running…</Typography>
          </Box>
        ) : !result || result.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#8A9199', border: '1px dashed #E0E3E7', borderRadius: '8px', background: '#FAFBFC' }}>
            <Box sx={{ fontSize: 30, color: '#D0D5DD', mb: 1 }}>↻</Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1f2328', mb: 0.5 }}>
              {result && result.length === 0 ? 'No results returned' : 'No results yet'}
            </Typography>
            <Typography sx={{ fontSize: '13px' }}>
              Fill the form and click <strong>Assign shifts</strong> to see the result here.
            </Typography>
          </Box>
        ) : (
          <>
            {/* summary */}
            <Grid container spacing={2} sx={{ mb: 2.5, maxWidth: 520 }}>
              {(() => {
                const total = result.length;
                const filled = result.filter((r) => r.filled).length;
                return [
                  { k: 'Total', v: total, c: '#1f2328', bg: '#F7F8FA', bd: '#EDEFF2' },
                  { k: 'Filled', v: filled, c: '#01784F', bg: 'rgba(1,120,79,.08)', bd: 'rgba(1,120,79,.25)' },
                  { k: 'Skipped', v: total - filled, c: '#B26A00', bg: 'rgba(178,106,0,.08)', bd: 'rgba(178,106,0,.3)' },
                ];
              })().map((t) => (
                <Grid item xs={4} key={t.k}>
                  <Box sx={{ background: t.bg, border: `1px solid ${t.bd}`, borderRadius: '8px', p: 1.75, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '26px', fontWeight: 800, color: t.c, lineHeight: 1.1 }}>{t.v}</Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: t.c === '#1f2328' ? '#8A9199' : t.c, textTransform: 'uppercase', letterSpacing: '.4px' }}>{t.k}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* table */}
            <Box sx={{ border: '1px solid #EDEFF2', borderRadius: '8px', overflow: 'hidden' }}>
              <Box sx={{ maxHeight: 460, overflowY: 'auto' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr .9fr 2.6fr', background: '#F4F4F4', position: 'sticky', top: 0, zIndex: 1 }}>
                {['Date', 'Shift', 'Status', 'Reason'].map((h) => (
                  <Box key={h} sx={{ p: '11px 14px', fontSize: '11px', fontWeight: 700, color: '#48535B', textTransform: 'uppercase', letterSpacing: '.3px' }}>
                    {h}
                  </Box>
                ))}
              </Box>
              {result.map((r, i) => {
                const status = r.filled ? 'FILLED' : 'SKIPPED';
                const shiftName = r.shift_code
                  ? (shiftTypes.find((s) => s.code === r.shift_code)?.label || shiftLabel(r.shift_code))
                  : '—';
                const removedNames = (r.removed || []).map((m) => displayMemberWithTime(m, employeeOptions));
                const occupiedNames = (r.occupied || []).map((m) => displayMemberWithTime(m, employeeOptions));
                const reason = r.skip_reason
                  ? r.skip_reason
                  : removedNames.length
                    ? `Removed: ${removedNames.join(', ')}`
                    : occupiedNames.length
                      ? `Occupied by: ${occupiedNames.join(', ')}`
                      : '—';
                return (
                  <Box key={i} sx={{
                    display: 'grid', gridTemplateColumns: '1.1fr 1fr .9fr 2.6fr', borderTop: '1px solid #F0F1F3', fontSize: '14px',
                    backgroundColor: r.filled ? 'transparent' : 'rgba(178,106,0,.03)',
                    '&:hover': { backgroundColor: '#FAFBFC' },
                  }}>
                    <Box sx={{ p: '11px 14px', color: '#1f2328' }}>
                      {r.shift_date ? moment(r.shift_date).format('ddd, MMM D, YYYY') : '—'}
                    </Box>
                    <Box sx={{ p: '11px 14px', color: '#48535B' }}>{shiftName}</Box>
                    <Box sx={{ p: '11px 14px' }}><StatusBadge status={status} /></Box>
                    <Box sx={{ p: '11px 14px', color: reason === '—' ? '#B0B6BD' : '#48535B' }}>{reason}</Box>
                  </Box>
                );
              })}
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default RecurringPositionAssignment;
